import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tags, RefreshCw, Loader2, Save, RotateCcw, CheckCircle2, XCircle, AlertTriangle, Info, X,
  Brain, Mic, BookOpen, Percent, Clock, Sparkles, History,
} from 'lucide-react';
import { billingApi, formatPaise } from '../NextGenBuilder/api/billingApi';

/**
 * FeaturePricingAdmin — /superadmin/feature-pricing
 *
 * One screen for the business rules of every billable TSG Nexus feature:
 *
 *   AI Tutor        free prompts per tool per month · credits per purchase · price
 *   Mock Interview  free interviews per month · interviews per purchase · price
 *   Elite Mastery   free share (% of published pages, minimum) · premium price ·
 *                   access validity (value + unit) · one technology per purchase
 *   GST             the default tax rate applied at checkout
 *
 * Every value is read from GET /billing/admin/feature-pricing and written back
 * with PUT /billing/admin/feature-pricing/:feature (or /tax). The server owns
 * validation, auditing (billing_config_audit), cache invalidation and
 * optimistic concurrency (expectedVersion → 409). This file renders the
 * database and never hardcodes a price, a quota or a percentage.
 */

const getUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.userId || user?.user_id || localStorage.getItem('user_id') || null;
  } catch {
    return localStorage.getItem('user_id') || null;
  }
};

const FEATURE_UI = {
  AI_TUTOR: { icon: Brain, tone: 'text-violet-600 bg-violet-50 border-violet-100', quotaLabel: 'Free prompts per tool per month', unitsLabel: 'Prompt credits per purchase' },
  MOCK_INTERVIEW: { icon: Mic, tone: 'text-cyan-700 bg-cyan-50 border-cyan-100', quotaLabel: 'Free interviews per month', unitsLabel: 'Interviews per purchase' },
  ELITE_MASTERY: { icon: BookOpen, tone: 'text-amber-700 bg-amber-50 border-amber-100', quotaLabel: 'Free pages per technology', unitsLabel: 'Units per purchase' },
};
const ORDER = ['AI_TUTOR', 'MOCK_INTERVIEW', 'ELITE_MASTERY'];

/* ── helpers ───────────────────────────────────────────────────────────── */

const paiseToRupeeText = (paise) => (paise == null || !Number.isFinite(Number(paise)) ? '' : (Number(paise) / 100).toFixed(2).replace(/\.00$/, ''));
const rupeeTextToPaise = (text) => {
  const t = String(text ?? '').trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Math.round(Number(t) * 100);
};
const bpsToPercentText = (bps) => (bps == null ? '' : String(Number(bps) / 100));
const percentTextToBps = (text) => {
  const t = String(text ?? '').trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Math.round(Number(t) * 100);
};
const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const errMessage = (e) => (e && (e.message || e.code)) || 'Request failed';

/** Same rule as the backend's freeAccess.freeAllowance — for the live preview only. */
export const previewFreePages = (mode, percent, minPages, fixedQuota, totalPages) => {
  if (totalPages == null) return null;
  if (mode !== 'PERCENT') return Math.min(totalPages, Math.max(0, Number(fixedQuota) || 0));
  if (totalPages === 0) return 0;
  const pct = Number(percent) || 0;
  const computed = pct > 0 ? Math.floor((totalPages * pct) / 100) : 0;
  const min = Math.max(0, Number(minPages) || 0);
  return Math.min(totalPages, Math.max(computed, Math.min(min, totalPages)));
};

/** Build the editable draft of one feature card from the server payload. */
export const draftFromFeature = (feature) => {
  const product = feature.products.find((p) => p.code === feature.primaryProduct) || feature.products.find((p) => p.purchasable) || null;
  return {
    name: feature.name || '',
    isActive: !!feature.isActive,
    freeQuota: feature.freeQuota == null ? '' : String(feature.freeQuota),
    freeAccessMode: feature.freeAccess?.mode || 'FIXED',
    freeAccessPercent: feature.freeAccess?.percent == null ? '' : String(feature.freeAccess.percent),
    freeQuotaMin: feature.freeAccess?.minUnits == null ? '' : String(feature.freeAccess.minUnits),
    freeTrialDescription: feature.display?.freeTrialDescription || '',
    premiumDescription: feature.display?.premiumDescription || '',
    ctaLabel: feature.display?.ctaLabel || '',
    productCode: product ? product.code : null,
    productName: product ? product.name || '' : '',
    productDescription: product ? product.description || '' : '',
    priceRupees: product ? paiseToRupeeText(product.basePricePaise) : '',
    unitsPerPurchase: product ? String(product.unitsPerPurchase ?? '') : '',
    maxQuantityPerOrder: product ? String(product.maxQuantityPerOrder ?? '') : '',
    accessType: product ? product.accessType : 'UNITS',
    validityValue: product && product.validity ? String(product.validity.value) : '',
    validityUnit: product && product.validity ? product.validity.unit : 'MONTH',
    pricingMode: product ? product.pricingMode : 'FIXED',
    requiresTechnology: !!(product && product.requiresTechnology),
  };
};

/** Client-side validation (mirrors the server; the server remains authoritative). */
export const validateDraft = (d) => {
  const errors = {};
  if (!d.name.trim()) errors.name = 'Display name is required';
  const intField = (key, label, min, max) => {
    if (d[key] === '' || d[key] == null) return;
    const n = Number(d[key]);
    if (!Number.isInteger(n)) errors[key] = `${label} must be a whole number`;
    else if (n < min) errors[key] = `${label} must be at least ${min}`;
    else if (max != null && n > max) errors[key] = `${label} must be at most ${max}`;
  };
  if (d.freeAccessMode === 'PERCENT') {
    const p = Number(d.freeAccessPercent);
    if (d.freeAccessPercent === '' || !Number.isFinite(p) || p < 0 || p > 100) errors.freeAccessPercent = 'Enter a percentage between 0 and 100';
    else if (Math.round(p * 100) !== p * 100) errors.freeAccessPercent = 'Use at most two decimal places';
    intField('freeQuotaMin', 'Minimum free pages', 0, 100000);
  } else {
    if (d.freeQuota === '') errors.freeQuota = 'Free allowance is required';
    intField('freeQuota', 'Free allowance', 0, 1000000);
  }
  if (d.productCode) {
    if (!d.productName.trim()) errors.productName = 'Product name is required';
    if (d.pricingMode === 'FIXED') {
      const paise = rupeeTextToPaise(d.priceRupees);
      if (paise === null) errors.priceRupees = 'Price is required';
      else if (Number.isNaN(paise) || paise < 0) errors.priceRupees = 'Enter a valid amount in rupees (up to 2 decimals)';
    }
    if (d.unitsPerPurchase === '') errors.unitsPerPurchase = 'Required';
    intField('unitsPerPurchase', 'Units per purchase', 1, 1000000);
    if (d.maxQuantityPerOrder === '') errors.maxQuantityPerOrder = 'Required';
    intField('maxQuantityPerOrder', 'Max quantity per order', 1, 1000);
    if (d.accessType === 'FULL_ACCESS' || d.validityValue !== '') {
      if (d.validityValue === '') errors.validityValue = 'Full access needs a validity period';
      intField('validityValue', 'Validity', 1, 1200);
      if (!['DAY', 'MONTH'].includes(d.validityUnit)) errors.validityUnit = 'Choose days or months';
    }
  }
  return errors;
};

/** Diff draft vs the original and produce the PUT body (only changed fields). */
export const buildPayload = (draft, original, expectedVersion) => {
  const feature = {};
  const product = {};
  const put = (target, key, next, prev) => { if (next !== prev) target[key] = next; };
  put(feature, 'name', draft.name.trim(), original.name.trim());
  put(feature, 'isActive', draft.isActive, original.isActive);
  if (draft.freeAccessMode === 'PERCENT') {
    put(feature, 'freeAccessMode', 'PERCENT', original.freeAccessMode);
    put(feature, 'freeAccessPercent', Number(draft.freeAccessPercent), original.freeAccessPercent === '' ? null : Number(original.freeAccessPercent));
    put(feature, 'freeQuotaMin', draft.freeQuotaMin === '' ? 0 : Number(draft.freeQuotaMin), original.freeQuotaMin === '' ? null : Number(original.freeQuotaMin));
  } else {
    put(feature, 'freeAccessMode', 'FIXED', original.freeAccessMode);
    put(feature, 'freeQuota', Number(draft.freeQuota), original.freeQuota === '' ? null : Number(original.freeQuota));
  }
  put(feature, 'freeTrialDescription', draft.freeTrialDescription.trim() || null, original.freeTrialDescription.trim() || null);
  put(feature, 'premiumDescription', draft.premiumDescription.trim() || null, original.premiumDescription.trim() || null);
  put(feature, 'ctaLabel', draft.ctaLabel.trim() || null, original.ctaLabel.trim() || null);
  if (draft.productCode) {
    put(product, 'name', draft.productName.trim(), original.productName.trim());
    put(product, 'description', draft.productDescription.trim() || null, original.productDescription.trim() || null);
    if (draft.pricingMode === 'FIXED') put(product, 'basePricePaise', rupeeTextToPaise(draft.priceRupees), rupeeTextToPaise(original.priceRupees));
    put(product, 'unitsPerPurchase', Number(draft.unitsPerPurchase), Number(original.unitsPerPurchase));
    put(product, 'maxQuantityPerOrder', Number(draft.maxQuantityPerOrder), Number(original.maxQuantityPerOrder));
    const nextV = draft.validityValue === '' ? null : Number(draft.validityValue);
    const prevV = original.validityValue === '' ? null : Number(original.validityValue);
    const nextU = nextV == null ? null : draft.validityUnit;
    const prevU = prevV == null ? null : original.validityUnit;
    if (nextV !== prevV || nextU !== prevU) { product.validityValue = nextV; product.validityUnit = nextU; }
  }
  const body = { expectedVersion };
  if (Object.keys(feature).length) body.feature = feature;
  if (Object.keys(product).length) body.product = { code: draft.productCode, ...product };
  return body;
};

/* ── UI primitives ─────────────────────────────────────────────────────── */

const INPUT = 'w-full px-2.5 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF] disabled:bg-gray-50 disabled:text-gray-400';

const Field = ({ label, hint, error, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
    {children}
    {hint && !error && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    {error && <span className="block text-[11px] text-red-600 mt-1 font-medium" role="alert">{error}</span>}
  </label>
);

const TextInput = ({ error, suffix, prefix, ...props }) => (
  <div className="relative">
    {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">{prefix}</span>}
    <input {...props} className={`${INPUT} ${error ? 'border-red-300 bg-red-50/40' : 'border-gray-200'} ${prefix ? 'pl-6' : ''} ${suffix ? 'pr-16' : ''}`} />
    {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">{suffix}</span>}
  </div>
);

const Toggle = ({ checked, onChange, label, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
    className={`inline-flex items-center gap-2 text-xs font-semibold ${checked ? 'text-emerald-700' : 'text-gray-500'} disabled:opacity-50`}>
    <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
    {label}
  </button>
);

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback((tone, message) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((t) => [...t, { id, tone, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 7000 : 4000);
  }, []);
  return { toasts, dismiss, success: (m) => push('success', m), error: (m) => push('error', m) };
};

const ToastStack = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-5 right-5 z-[60] space-y-2 w-[min(24rem,calc(100vw-2.5rem))]" aria-live="polite">
    {toasts.map((t) => {
      const Icon = t.tone === 'error' ? XCircle : CheckCircle2;
      return (
        <div key={t.id} className={`${t.tone === 'error' ? 'bg-red-600' : 'bg-emerald-600'} text-white rounded-xl shadow-lg px-4 py-3 flex items-start gap-2 text-sm`}>
          <Icon size={16} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1 break-words">{t.message}</span>
          <button onClick={() => onDismiss(t.id)} className="opacity-70 hover:opacity-100" aria-label="Dismiss"><X size={14} /></button>
        </div>
      );
    })}
  </div>
);

/* ── one feature card ──────────────────────────────────────────────────── */

const FeatureCard = ({ feature, version, technologies, currency, onSaved, onConflict, toast }) => {
  const ui = FEATURE_UI[feature.code] || { icon: Sparkles, tone: 'text-gray-700 bg-gray-50 border-gray-100', quotaLabel: 'Free allowance', unitsLabel: 'Units per purchase' };
  const Icon = ui.icon;
  const original = useMemo(() => draftFromFeature(feature), [feature]);
  const [draft, setDraft] = useState(original);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(original); setServerErrors({}); }, [original]);

  const set = (key) => (value) => { setDraft((d) => ({ ...d, [key]: value })); setServerErrors((e) => (e[key] ? { ...e, [key]: undefined } : e)); };
  const onText = (key) => (e) => set(key)(e.target.value);
  const errors = useMemo(() => ({ ...validateDraft(draft), ...Object.fromEntries(Object.entries(serverErrors).filter(([, v]) => v)) }), [draft, serverErrors]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(original), [draft, original]);
  const isElite = feature.code === 'ELITE_MASTERY';
  const product = feature.products.find((p) => p.code === draft.productCode) || null;
  const pricePaise = rupeeTextToPaise(draft.priceRupees);

  const save = async () => {
    if (Object.keys(errors).length) { toast.error('Please fix the highlighted fields'); return; }
    const body = buildPayload(draft, original, version);
    if (!body.feature && !body.product) return;
    setSaving(true);
    try {
      const data = await billingApi.admin.updateFeaturePricing(getUserId(), feature.code, body);
      onSaved(data, `${draft.name.trim()} pricing saved`);
    } catch (e) {
      if (e.status === 409) { onConflict(e); return; }
      if (e.status === 422 && e.payload && e.payload.fields) {
        const mapped = {};
        Object.entries(e.payload.fields).forEach(([k, msg]) => {
          const key = k.replace(/^feature\./, '').replace(/^product\.(name|description)$/, (m, f) => (f === 'name' ? 'productName' : 'productDescription')).replace(/^product\./, '').replace(/^basePricePaise$/, 'priceRupees');
          mapped[key] = msg;
        });
        setServerErrors(mapped);
        toast.error(e.message || 'Validation failed');
      } else toast.error(errMessage(e));
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4" data-testid={`feature-card-${feature.code}`} aria-labelledby={`fp-${feature.code}-title`}>
      <header className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${ui.tone}`}><Icon size={20} /></div>
        <div className="min-w-0 flex-1">
          <h2 id={`fp-${feature.code}-title`} className="text-base font-bold text-gray-900 leading-tight">{feature.name}</h2>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{feature.code} · {feature.quotaScope === 'SUB_FEATURE' ? 'per tool' : feature.quotaScope === 'TECHNOLOGY' ? 'per technology' : 'per account'} · {feature.quotaPeriod === 'MONTH' ? 'resets monthly' : 'never resets'}</p>
        </div>
        <Toggle checked={draft.isActive} onChange={set('isActive')} label={draft.isActive ? 'Active' : 'Disabled'} disabled={saving} />
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Display name" error={errors.name} className="sm:col-span-2">
          <TextInput value={draft.name} onChange={onText('name')} error={errors.name} disabled={saving} />
        </Field>

        {isElite ? (
          <>
            <Field label="Free share of published pages" error={errors.freeAccessPercent} hint="Free pages = floor(total pages × %). Recomputed automatically as content is published.">
              <TextInput inputMode="decimal" value={draft.freeAccessPercent} onChange={onText('freeAccessPercent')} error={errors.freeAccessPercent} suffix="% of pages" disabled={saving} aria-label="Free page percentage" />
            </Field>
            <Field label="Minimum free pages" error={errors.freeQuotaMin} hint="Applied when the percentage rounds down to fewer pages (if content exists).">
              <TextInput inputMode="numeric" value={draft.freeQuotaMin} onChange={onText('freeQuotaMin')} error={errors.freeQuotaMin} suffix="pages" disabled={saving} aria-label="Minimum free pages" />
            </Field>
          </>
        ) : (
          <Field label={ui.quotaLabel} error={errors.freeQuota} className="sm:col-span-2">
            <TextInput inputMode="numeric" value={draft.freeQuota} onChange={onText('freeQuota')} error={errors.freeQuota} suffix={feature.unitLabelPlural} disabled={saving} aria-label={ui.quotaLabel} />
          </Field>
        )}
      </div>

      {product ? (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3.5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex items-center justify-between">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Paid product · {product.code}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${draft.accessType === 'FULL_ACCESS' ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-800'}`}>
              {draft.accessType === 'FULL_ACCESS' ? 'Full access · time-boxed' : 'Credit wallet'}
            </span>
          </div>
          <Field label="Product name" error={errors.productName} className="sm:col-span-2">
            <TextInput value={draft.productName} onChange={onText('productName')} error={errors.productName} disabled={saving} />
          </Field>
          <Field label={`Price (${currency}, before GST)`} error={errors.priceRupees} hint={draft.pricingMode !== 'FIXED' ? 'Priced by tiers — edit in Nexus Billing → Elite pricing tiers.' : (pricePaise != null && !Number.isNaN(pricePaise) ? `Stored as ${pricePaise} paise · ${formatPaise(pricePaise)} + GST at checkout` : undefined)}>
            <TextInput inputMode="decimal" value={draft.priceRupees} onChange={onText('priceRupees')} error={errors.priceRupees} prefix="₹" disabled={saving || draft.pricingMode !== 'FIXED'} aria-label="Price in rupees" />
          </Field>
          {draft.accessType === 'FULL_ACCESS' ? (
            <Field label="Access validity" error={errors.validityValue || errors.validityUnit} hint="Counted from payment; a repurchase while active extends the current expiry.">
              <div className="flex gap-2">
                <TextInput inputMode="numeric" value={draft.validityValue} onChange={onText('validityValue')} error={errors.validityValue} disabled={saving} aria-label="Validity value" />
                <select value={draft.validityUnit} onChange={onText('validityUnit')} disabled={saving} aria-label="Validity unit" className={`${INPUT} border-gray-200 w-32`}>
                  <option value="MONTH">months</option>
                  <option value="DAY">days</option>
                </select>
              </div>
            </Field>
          ) : (
            <Field label={ui.unitsLabel} error={errors.unitsPerPurchase}>
              <TextInput inputMode="numeric" value={draft.unitsPerPurchase} onChange={onText('unitsPerPurchase')} error={errors.unitsPerPurchase} suffix={feature.unitLabelPlural} disabled={saving} aria-label={ui.unitsLabel} />
            </Field>
          )}
          <Field label="Max quantity per order" error={errors.maxQuantityPerOrder} hint={draft.requiresTechnology ? 'One technology per purchase — the technology is chosen at purchase time.' : undefined}>
            <TextInput inputMode="numeric" value={draft.maxQuantityPerOrder} onChange={onText('maxQuantityPerOrder')} error={errors.maxQuantityPerOrder} disabled={saving} aria-label="Max quantity per order" />
          </Field>
          <Field label="Product description" className="sm:col-span-2">
            <TextInput value={draft.productDescription} onChange={onText('productDescription')} disabled={saving} />
          </Field>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 p-3 text-xs text-gray-500 flex items-center gap-2"><Info size={14} /> No purchasable product is configured for this feature.</div>
      )}

      {isElite && (
        <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">Live preview per technology</span>
            <span className="text-[10px] text-amber-700">{dirty ? 'preview reflects unsaved values' : 'current published counts'}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="elite-preview">
              <thead><tr className="text-left text-gray-500"><th className="py-1 pr-2 font-semibold">Technology</th><th className="py-1 pr-2 font-semibold text-right">Published</th><th className="py-1 pr-2 font-semibold text-right">Free</th><th className="py-1 font-semibold text-right">Premium</th></tr></thead>
              <tbody>
                {technologies.map((t) => {
                  const free = previewFreePages(draft.freeAccessMode, draft.freeAccessPercent, draft.freeQuotaMin, draft.freeQuota, t.totalPages);
                  return (
                    <tr key={t.slug} className="border-t border-amber-100/70">
                      <td className="py-1 pr-2 text-gray-800 font-medium">{t.title}</td>
                      <td className="py-1 pr-2 text-right text-gray-700">{t.totalPages == null ? <span className="text-gray-400">not published</span> : t.totalPages}</td>
                      <td className="py-1 pr-2 text-right text-emerald-700 font-semibold">{free == null ? '—' : free}</td>
                      <td className="py-1 text-right text-amber-800 font-semibold">{free == null || t.totalPages == null ? '—' : t.totalPages - free}</td>
                    </tr>
                  );
                })}
                {technologies.length === 0 && <tr><td colSpan={4} className="py-2 text-gray-400">No eligible technologies.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <Field label="Free trial description (shown to learners)"><TextInput value={draft.freeTrialDescription} onChange={onText('freeTrialDescription')} disabled={saving} /></Field>
        <Field label="Premium description (shown to learners)"><TextInput value={draft.premiumDescription} onChange={onText('premiumDescription')} disabled={saving} /></Field>
        <Field label="Call-to-action label"><TextInput value={draft.ctaLabel} onChange={onText('ctaLabel')} disabled={saving} /></Field>
      </div>

      <footer className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100 mt-auto">
        <span className="text-[11px] text-gray-400 flex items-center gap-1 min-w-0" title={feature.lastUpdated ? `by ${feature.lastUpdated.by}` : ''}>
          <History size={12} className="flex-shrink-0" />
          <span className="truncate">{feature.lastUpdated ? `Updated ${formatDateTime(feature.lastUpdated.at)} by ${feature.lastUpdated.by || 'system'}` : 'No changes recorded yet'}</span>
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={() => { setDraft(original); setServerErrors({}); }} disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            <RotateCcw size={13} /> Cancel
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving || Object.keys(errors).length > 0}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-lg bg-[#843FEF] text-white hover:bg-[#7433d6] disabled:opacity-40">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </footer>
    </section>
  );
};

/* ── tax card ──────────────────────────────────────────────────────────── */

const TaxCard = ({ taxRate, version, onSaved, onConflict, toast }) => {
  const original = bpsToPercentText(taxRate.rateBps);
  const [value, setValue] = useState(original);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(original); }, [original]);
  const bps = percentTextToBps(value);
  const error = value === '' ? 'GST rate is required' : (Number.isNaN(bps) || bps < 0 || bps > 10000 ? 'Enter a percentage between 0 and 100 (up to 2 decimals)' : null);
  const dirty = value !== original;
  const save = async () => {
    if (error) return;
    setSaving(true);
    try {
      const data = await billingApi.admin.updateTaxRate(getUserId(), { rateBps: bps, expectedVersion: version });
      onSaved(data, `GST rate saved (${value}%)`);
    } catch (e) {
      if (e.status === 409) { onConflict(e); return; }
      toast.error(errMessage(e));
    } finally { setSaving(false); }
  };
  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4" aria-labelledby="fp-tax-title">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 text-emerald-700 bg-emerald-50 border-emerald-100"><Percent size={20} /></div>
        <div className="min-w-0 flex-1">
          <h2 id="fp-tax-title" className="text-base font-bold text-gray-900 leading-tight">{taxRate.name || 'GST'}</h2>
          <p className="text-xs text-gray-500 mt-0.5">Applied on top of every price at checkout · {taxRate.code}</p>
        </div>
      </header>
      <Field label="Tax rate" error={error} hint={!error ? `Stored as ${bps} basis points. Existing orders keep the rate they were invoiced at.` : undefined}>
        <TextInput inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} error={error} suffix="%" disabled={saving} aria-label="GST percentage" />
      </Field>
      <footer className="flex items-center justify-between gap-3 pt-1 border-t border-gray-100 mt-auto">
        <span className="text-[11px] text-gray-400 flex items-center gap-1 min-w-0"><Clock size={12} /><span className="truncate">{taxRate.lastUpdated ? `Updated ${formatDateTime(taxRate.lastUpdated.at)} by ${taxRate.lastUpdated.by || 'system'}` : 'No changes recorded yet'}</span></span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setValue(original)} disabled={!dirty || saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"><RotateCcw size={13} /> Cancel</button>
          <button type="button" onClick={save} disabled={!dirty || saving || !!error} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-lg bg-[#843FEF] text-white hover:bg-[#7433d6] disabled:opacity-40">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </footer>
    </section>
  );
};

/* ── page ──────────────────────────────────────────────────────────────── */

const FeaturePricingAdmin = () => {
  const toast = useToasts();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [conflict, setConflict] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try { setData(await billingApi.admin.getFeaturePricing(getUserId())); setConflict(null); } catch (e) { setLoadError(errMessage(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = useCallback((fresh, message) => { setData(fresh); setConflict(null); toast.success(message); }, [toast]);
  const onConflict = useCallback((e) => { setConflict(e.message || 'This configuration was changed by someone else. Reload to see the latest values.'); }, []);

  const features = useMemo(() => (data ? [...data.features].sort((a, b) => (ORDER.indexOf(a.code) === -1 ? 99 : ORDER.indexOf(a.code)) - (ORDER.indexOf(b.code) === -1 ? 99 : ORDER.indexOf(b.code))) : []), [data]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#843FEF] text-white flex items-center justify-center"><Tags size={22} /></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Feature Pricing</h1>
            <p className="text-sm text-gray-500">Free allowances, prices, access validity and GST — the database is the single source of truth; learners see exactly what is saved here.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-1" title="billing.config_version">config v{data.version}</span>}
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Reload
          </button>
        </div>
      </div>

      {conflict && (
        <div role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{conflict}</span>
          <button type="button" onClick={load} className="text-xs font-bold underline">Reload now</button>
        </div>
      )}
      {loadError && !data && (
        <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 flex items-center gap-2"><XCircle size={16} /> {loadError} <button type="button" onClick={load} className="ml-auto text-xs font-bold underline">Retry</button></div>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 size={18} className="animate-spin" /> Loading configuration…</div>
      ) : data ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
          {features.map((f) => (
            <FeatureCard key={f.code} feature={f} version={data.version} technologies={data.technologies || []} currency={data.currency || 'INR'} onSaved={onSaved} onConflict={onConflict} toast={toast} />
          ))}
          <TaxCard taxRate={data.taxRate} version={data.version} onSaved={onSaved} onConflict={onConflict} toast={toast} />
        </div>
      ) : null}

      <ToastStack toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  );
};

export default FeaturePricingAdmin;
